import React from 'react';
import { Column } from 'react-table';
import SessionTable, {
  ResponseGuard,
  SessionTableInstanceProps
} from '../index';
import {
  MappingCell,
  DirectCell,
  YesNoCell,
  CellFunction,
  NAGuard
} from '../../cells';
import Tooltip from '../../../tooltip';
import Icon from '../../../../atoms/icon';
import { IEpxPedOnc } from '../../../../../libs/apps/epxpedonc';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { useMetaData } from '../../../../../contexts/metadata';

const MedicationCell: CellFunction<IEpxPedOnc> = ({
  row: { original: session }
}) => {
  const schedule = session.schedule;
  const medications = schedule.medications;
  let label = '?';
  for (let i = 0; i < medications.length; i++) {
    if (session.muid === medications[i].uid) {
      label = medications[i].name;
    }
  }
  return <div>{label}</div>;
};

type Type = 'checkup' | 'housing';

interface EpxPedOncTableProps extends SessionTableInstanceProps {
  type?: Type;
}

const GetNACheckOut = () => {
  return (session: IEpxPedOnc): boolean => {
    return session.missedReason !== 'Out of Meds';
  };
};

const GetNACheckStopped = () => {
  return (session: IEpxPedOnc): boolean => {
    return session.missedReason !== 'Stopped Taking';
  };
};

const EpxPedOncTable: React.FC<EpxPedOncTableProps> = ({
  type,
  ...props
}): JSX.Element => {
  const { terms } = useMetaData();
  let columns: Column<IEpxPedOnc>[] = [
    {
      Header: 'Medication',
      id: 'muid',
      Cell: MedicationCell
    },
    {
      Header: 'Took Medication',
      id: 'tookMedication',
      Cell: ResponseGuard(
        YesNoCell('tookMedication') as CellFunction<IEpxPedOnc>
      )
    },
    {
      Header: 'Missed Reason',
      id: 'missedReason',
      Cell: ResponseGuard(
        DirectCell('missedReason') as CellFunction<IEpxPedOnc>
      )
    },
    {
      Header: 'Out of Med Reason',
      id: 'outOfMedsReason',
      Cell: NAGuard(
        ResponseGuard(
          DirectCell('outOfMedsReason') as CellFunction<IEpxPedOnc>
        ),
        GetNACheckOut()
      )
    },
    {
      Header: (
        <>
          Stopped Taking Med Reason
          <Tooltip
            content={`If the ${terms.patient.default} responds with 'Doctor Instruction' or 'Dosage Changed', then the medication will be discontinued.`}
          >
            <Icon className="ml-2" icon={faInfoCircle} />
          </Tooltip>
        </>
      ),
      id: 'stoppedTakingMedsReason',
      Cell: NAGuard(
        ResponseGuard(
          DirectCell('stoppedTakingMedsReason') as CellFunction<IEpxPedOnc>
        ),
        GetNACheckStopped()
      )
    }
  ];
  if (type === 'checkup') {
    columns = [
      {
        Header: 'Would Like to Chat',
        id: 'checkupCall',
        Cell: ResponseGuard(
          YesNoCell('checkupCall') as CellFunction<IEpxPedOnc>
        )
      },
      {
        Header: 'Feeling Compared to Usual',
        id: 'checkupStatus',
        Cell: ResponseGuard(
          MappingCell('checkupStatus', {
            1: 'Better',
            2: 'Same',
            3: 'Worse'
          }) as CellFunction<IEpxPedOnc>
        )
      }
    ];
  } else if (type === 'housing') {
    columns = [
      {
        Header: 'Would Like to Chat abount Housing/Bills',
        id: 'housingTalk',
        Cell: ResponseGuard(
          YesNoCell('housingTalk') as CellFunction<IEpxPedOnc>
        )
      },
      {
        Header: 'Opt-Out',
        id: 'housingDiscontinued',
        Cell: ResponseGuard(
          YesNoCell('housingDiscontinued') as CellFunction<IEpxPedOnc>
        )
      }
    ];
  }
  let url = '/epxpedonc/data/';
  if (type === 'housing' || type === 'checkup') {
    url = `/epxpedonc/data/?type=${type}`;
  }
  return <SessionTable<IEpxPedOnc> url={url} columns={columns} {...props} />;
};

export default EpxPedOncTable;
